import { reactive, readonly } from 'vue'
import { Message } from '@arco-design/web-vue'
import router from '@/router'
import type { GlobalWsMessage } from '@/api/modules/globalWebsocket'
import { getGlobalWebSocketStudentId } from '@/api/modules/globalWebsocket'
import { fetchStudentRushNotify, type RushNotifyTask } from '@/api/modules/raceAnswer'

const RACE_ANSWER_POPUP_BLOCKED_ROUTES = ['raceAnswer']

export interface RaceAnswerNotifyPayload {
  taskType: 'RUSH' | 'CALL'
  taskId: number
  roundId: number
  roundNum: number
  startTime?: number | string
  content: string
  resourceId?: number
  resourceFileName?: string
  courseName: string
  classId: number
  phaseType: 'START' | 'STOP'
}

const raceAnswerNotifyState = reactive({
  visible: false,
  payload: null as RaceAnswerNotifyPayload | null
})

function openRaceAnswerNotifyModal(payload: RaceAnswerNotifyPayload) {
  raceAnswerNotifyState.payload = payload
  raceAnswerNotifyState.visible = true
}

function closeRaceAnswerNotifyModal() {
  raceAnswerNotifyState.visible = false
  raceAnswerNotifyState.payload = null
}

function isBlockedRaceAnswerRoute() {
  const routeName = String(router.currentRoute.value.name || '')
  return RACE_ANSWER_POPUP_BLOCKED_ROUTES.includes(routeName)
}

/** rush-notify 任务项转换为抢答弹窗 payload（仅 RUSH 场景） */
function buildRushPayloadFromTask(task: RushNotifyTask): RaceAnswerNotifyPayload {
  return {
    taskType: 'RUSH',
    taskId: task.taskId,
    roundId: task.roundId,
    roundNum: task.roundNum,
    startTime: task.startTime,
    content: task.content || '',
    resourceId: task.resourceId ?? undefined,
    resourceFileName: '',
    courseName: task.courseName || '',
    classId: task.classId,
    phaseType: 'START'
  }
}

/**
 * 进入系统时主动拉取抢答提醒并弹窗。
 * 与签到的差异：无 abnormal 分支；只展示「最后一条」有权限任务，而非逐条轮播。
 */
async function fetchAndOpenRaceAnswerNotifyModal() {
  try {
    const { success, content, message } = await fetchStudentRushNotify()
    if (!success) {
      Message.error(message)
      return { success, content, message }
    }
    const activeTaskList = (content?.activeTaskList || []).filter(item => item.hasPermission !== false)
    // 取最后一条（接口按 startTime 升序，末尾为最新一条任务）
    const lastTask = activeTaskList[activeTaskList.length - 1]
    if (!lastTask || isBlockedRaceAnswerRoute()) {
      return { success, content, message }
    }
    openRaceAnswerNotifyModal(buildRushPayloadFromTask(lastTask))
    return { success, content, message }
  } catch (error: any) {
    console.error(error)
    Message.error(error?.message || '获取抢答提醒失败')
    return {
      success: false,
      content: { activeTaskList: [] },
      message: error?.message || '获取抢答提醒失败'
    }
  }
}

function handleRaceAnswerNotification(message: GlobalWsMessage) {
  const payload = message.payload || message.data
  if (message.type !== 'NOTIFICATION' || !payload) return
  if (payload.taskType !== 'RUSH') return

  // phaseType 为 STOP 时，关闭对应 taskId 的弹窗
  if (payload.phaseType === 'STOP') {
    if (raceAnswerNotifyState.visible
      && raceAnswerNotifyState.payload
      && raceAnswerNotifyState.payload.taskId === payload.taskId) {
      closeRaceAnswerNotifyModal()
    }
    return
  }

  // phaseType 不为 START 时忽略
  if (payload.phaseType !== 'START') return
  const userId = getGlobalWebSocketStudentId()
  // 解决一下
  if (Array.isArray(payload.alreadyInteractList) && payload.alreadyInteractList.some(id => id == userId)) return

  // 已在抢答/点答详情页时不弹窗
  if (isBlockedRaceAnswerRoute()) {
    return
  }

  openRaceAnswerNotifyModal(payload as RaceAnswerNotifyPayload)
}

export function useRaceAnswerNotifyModal() {
  return {
    raceAnswerNotifyState: readonly(raceAnswerNotifyState),
    openRaceAnswerNotifyModal,
    closeRaceAnswerNotifyModal,
    fetchAndOpenRaceAnswerNotifyModal,
    handleRaceAnswerNotification
  }
}
